// frontend/src/components/production-sequence/sequenceFormat.ts
// Formato + paletas de badge compartidas entre la tabla y la vista de cards.
import type { ComponentStatus, OrderStatus } from '@/types/productionSequence';

export function fmtNum(n: number | null | undefined): string {
  return n == null ? '—' : n.toLocaleString('es-ES');
}

export function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  // Tolera datetime con hora ("2026-07-10T00:00:00") usando solo la parte de fecha.
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${y.slice(2)}`;
}

/**
 * El color de cada estado de material, en un solo sitio.
 *
 * Es el vocabulario que el responsable de producción fijó por escrito:
 *   verde   — está donde se va a consumir
 *   azul    — está en España, pero hay que moverlo de almacén
 *   naranja — no está en ningún almacén español, pero la compra viene de camino
 *   rojo    — no hay compra, o llega después de la fecha de producción
 *
 * El azul se ha MOVIDO de sitio, y ahí está el arreglo: antes vestía a
 * COMPRAS_EN_CURSO —«no está aquí, viene de camino»— y un azul no alarma a
 * nadie, así que ese caso se leía como si no pasara nada. Ahora viste a
 * ENTRE_ALMACENES, que es donde encaja: el material existe, sólo hay que
 * moverlo. Lo que todavía no está aquí va en naranja.
 *
 * La pantalla y el Excel salen los dos de esta tabla. Antes eran dos listas
 * paralelas con un comentario que decía «paleta alineada», que es un deseo y no
 * un mecanismo; `sequencePaleta.test.ts` lo convierte en uno.
 */
export const COMP_COLOR = {
  OK: 'emerald',
  PENDIENTE_CALIDAD: 'violet',
  ENTRE_ALMACENES: 'sky',
  COMPRAS_EN_CURSO: 'orange',
  ROTURA_STOCK: 'red',
} as const satisfies Record<ComponentStatus, string>;

// El relleno es SÓLIDO, y esa es la corrección: antes era el color al 15 % de
// opacidad sobre el fondo de la fila — un rojo que sobre blanco acaba en
// #fdeaea — y el resultado era un semáforo que había que ir a buscar. Los
// usuarios no se daban cuenta de que la línea estaba marcada, que es
// exactamente lo contrario de lo que hace un semáforo: la rotura tiene que
// saltar a la vista recorriendo la tabla de reojo, sin leerla.
//
// El texto va en blanco sobre el relleno, así que el color de la familia vive
// ahora en el FONDO y no en el texto. El punto del badge usa `bg-current`, que
// hereda ese blanco y sigue legible.
//
// El `border-<color>-600` no pinta nada donde no hay grosor de borde — está
// por el panel de filtros, cuyo botón sí lleva `border` y se quedaba con el
// borde neutro por encima del relleno de color.
//
// Las clases van literales y no interpoladas: Tailwind sólo genera el CSS de lo
// que encuentra escrito tal cual en el código.
export const COMP_BADGE: Record<ComponentStatus, string> = {
  OK: 'bg-emerald-600 text-white border-emerald-600',
  PENDIENTE_CALIDAD: 'bg-violet-600 text-white border-violet-600',
  ENTRE_ALMACENES: 'bg-sky-600 text-white border-sky-600',
  COMPRAS_EN_CURSO: 'bg-orange-600 text-white border-orange-600',
  ROTURA_STOCK: 'bg-red-600 text-white border-red-600',
};

/**
 * El tinte de FILA por estado de material — la subtabla de componentes.
 *
 * Mismo vocabulario que `COMP_BADGE`, en otra escala: el badge es una mancha
 * pequeña y sólida, la fila es una superficie y por eso va en el tono claro de
 * la familia. Vive aquí y no en el componente para que el chip y el fondo del
 * mismo estado no puedan acabar diciendo colores distintos.
 *
 * Lo usa la subtabla que se abre al desplegar una producción en Gantt
 * (`ConsumoSubTable`), que es donde se contesta «¿cuál de los diez componentes
 * me está frenando?». **No lo usa la fila de la producción**: ahí el aviso ya lo
 * dan el chip y el contador ⚠N, y teñir la cabecera entera tapaba el Gantt.
 *
 * **OK no tiñe.** Si se pintara, la fila que hay que mirar dejaría de destacar
 * entre las nueve que están bien: se señala lo que pide una acción.
 *
 * El borde va en `border-l-<color>` y no en `border-<color>`: la fila ya lleva
 * su `border-b` de separación, y un color de borde a secas se lo teñiría
 * también.
 */
export const COMP_ROW: Record<ComponentStatus, { claro: string; oscuro: string }> = {
  OK: { claro: '', oscuro: '' },
  PENDIENTE_CALIDAD: {
    claro:  'bg-violet-200 hover:bg-violet-300 border-l-4 border-l-violet-600',
    oscuro: 'bg-violet-900/35 hover:bg-violet-900/50 border-l-4 border-l-violet-500',
  },
  ENTRE_ALMACENES: {
    claro:  'bg-sky-200 hover:bg-sky-300 border-l-4 border-l-sky-600',
    oscuro: 'bg-sky-900/35 hover:bg-sky-900/50 border-l-4 border-l-sky-500',
  },
  COMPRAS_EN_CURSO: {
    claro:  'bg-orange-200 hover:bg-orange-300 border-l-4 border-l-orange-600',
    oscuro: 'bg-orange-900/35 hover:bg-orange-900/50 border-l-4 border-l-orange-500',
  },
  ROTURA_STOCK: {
    claro:  'bg-red-200 hover:bg-red-300 border-l-4 border-l-red-600',
    oscuro: 'bg-red-900/40 hover:bg-red-900/55 border-l-4 border-l-red-500',
  },
};

// Color por estado, keyed por el enum (estable entre idiomas — no por la etiqueta traducida).
export const STATUS_BADGE: Record<OrderStatus, string> = {
  TERMINADO: 'bg-slate-400/15 text-slate-500',
  NOTIFICADO_TERMINADO: 'bg-purple-500/15 text-purple-400',
  PROGRAMADO: 'bg-amber-500/15 text-amber-500',
  LIBERADO: 'bg-sky-500/15 text-sky-400',
  INICIADO: 'bg-emerald-500/15 text-emerald-500',
  ESTIMADO: 'bg-slate-500/15 text-slate-400',
};

// Clases base de un badge de estado (mismo tamaño/estilo que common/StatusBadge size="sm").
export const STATUS_BADGE_BASE =
  'inline-flex items-center rounded font-semibold leading-none text-[10px] px-1.5 py-0.5';
